import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AuthShell } from "../components/AuthShell";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Invalid or missing reset token");
    }
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Reset token missing — please request a new link.");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated! Redirecting to login...");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell variant="copper">
      <div className="mt-6 mb-6">
        <Link
          to="/login"
          data-testid="rp-back-to-login"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm"
        >
          <ArrowLeft size={16} /> Back to login
        </Link>
      </div>

      <h2 className="text-2xl font-bold mb-2">Set a new password</h2>
      <p className="text-white/60 text-sm mb-6">
        Choose a strong password you haven't used before. The reset link is valid for 1 hour.
      </p>

      {!token && (
        <div
          data-testid="rp-no-token"
          className="rounded-md bg-amber-500/10 border border-amber-400/30 px-4 py-3 text-amber-200 text-sm flex items-start gap-2 mb-4"
        >
          <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
          <span>
            No reset token found in the URL. Please open the link from your email, or{" "}
            <Link to="/forgot-password" className="underline">request a new reset link</Link>.
          </span>
        </div>
      )}

      {done ? (
        <div
          data-testid="rp-success"
          className="rounded-md bg-[#19a55c]/10 border border-[#19a55c]/30 px-4 py-4 text-[#7ddca7] flex items-start gap-3"
        >
          <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-white">Password updated</div>
            <div className="text-sm">You can now sign in with your new password.</div>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
          <div className="space-y-2">
            <label className="block text-base text-white/90" htmlFor="rp-pw">New password</label>
            <div className="relative">
              <input
                id="rp-pw"
                data-testid="rp-password-input"
                type={showPw ? "text" : "password"}
                placeholder="At least 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!token || submitting}
                className="w-full h-12 rounded-md px-4 pr-12 bg-[#1a2030] text-white placeholder:text-white/35 border border-white/5 focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/30 transition-colors disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                data-testid="rp-toggle-pw"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                tabIndex={-1}
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-base text-white/90" htmlFor="rp-pw2">Confirm password</label>
            <input
              id="rp-pw2"
              data-testid="rp-confirm-input"
              type={showPw ? "text" : "password"}
              placeholder="Re-enter new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={!token || submitting}
              className="w-full h-12 rounded-md px-4 bg-[#1a2030] text-white placeholder:text-white/35 border border-white/5 focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/30 transition-colors disabled:opacity-50"
            />
          </div>

          <button
            type="submit"
            disabled={!token || submitting}
            data-testid="rp-submit-btn"
            className="w-full h-12 rounded-md bg-[#19a55c] hover:bg-[#21c46e] active:bg-[#168f4f] text-white font-bold text-lg transition-colors flex items-center justify-center disabled:opacity-60"
          >
            {submitting ? <Loader2 className="animate-spin" size={20} /> : "Update password"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
